import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { Card, Field, Notice, PageHeader, Segmented } from '../../components/ui/Page';

type UserRole = 'ADMIN' | 'CUSTOMER';

export default function UserFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'CUSTOMER' as UserRole });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; role: UserRole; emailSent: boolean; temporaryPassword?: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post('/admin/users', form);
      setCreated({ email: data.user.email, role: data.user.role, emailSent: data.emailSent, temporaryPassword: data.temporaryPassword });
    } catch (err) {
      setError(apiError(err, 'Could not create the user.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader crumbs={[{ label: 'Users', to: '/users' }, { label: 'Create user' }]} title="Create user" description="Choose whether this account works as a Micus admin or a customer. The user sets a new password on first sign-in." />
      {created ? (
        <Card title="Account created">
          <p className="text-sm text-[#3b0a22]">{created.email} is now a {created.role === 'ADMIN' ? 'Micus admin' : 'customer'}.</p>
          {created.emailSent ? (
            <Notice className="mt-3">A sign-in invitation was sent to the user.</Notice>
          ) : (
            <Notice tone="warning" className="mt-3">
              The account was created, but email could not be sent. Provide this temporary password directly: <strong className="font-mono">{created.temporaryPassword}</strong>
            </Notice>
          )}
          <button type="button" className="mz-btn-primary mt-4" onClick={() => navigate('/users')}>Back to users</button>
        </Card>
      ) : (
        <Card>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="u-name">
              <input id="u-name" required minLength={2} maxLength={120} className="mz-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Phone" htmlFor="u-phone">
              <input id="u-phone" required minLength={5} maxLength={40} className="mz-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </Field>
            <Field label="Email" htmlFor="u-email" className="sm:col-span-2">
              <input id="u-email" required type="email" maxLength={200} className="mz-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </Field>
            <Field label="Role" className="sm:col-span-2">
              <Segmented
                value={form.role}
                onChange={(role) => setForm((f) => ({ ...f, role }))}
                options={[{ value: 'CUSTOMER', label: 'Customer' }, { value: 'ADMIN', label: 'Admin' }]}
              />
            </Field>
            {error && <Notice tone="error" className="sm:col-span-2">{error}</Notice>}
            <div className="flex justify-end gap-2 sm:col-span-2">
              <button type="button" className="mz-btn-ghost" onClick={() => navigate('/users')}>Cancel</button>
              <button type="submit" disabled={saving} className="mz-btn-primary">{saving ? 'Creating…' : 'Create user & send invite'}</button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}